import clsx from 'clsx';
import { type Snippet } from 'svelte';
import type { Action } from 'svelte/action';
import { type DeviceName } from '@athena-lib/device';
import type { LinkData } from '@athena-lib/Link';
import type { HTMLButtonAttributes } from 'svelte/elements';
import type { ClickEvent } from '@athena-lib/types';

export const buttonSizes = ['medium', 'small', 'large', 'content', 'circle'] as const;
export type ButtonSize = (typeof buttonSizes)[number];
export type ResponsiveButtonSize = Partial<Record<DeviceName, ButtonSize>>;

export const buttonColors = ['white', 'brand', 'dark', 'transparent'] as const;
export type ButtonColor = (typeof buttonColors)[number];

export type ButtonProps = {
  onClick?: (e: ClickEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  children: Snippet;
  size?: ButtonSize | ResponsiveButtonSize;
  class?: string;
  full?: boolean;
  wide?: boolean;
  color?: ButtonColor;
  element?: string;
  link?: LinkData;
  disabled?: boolean;
  action?: Action<HTMLDivElement, NonNullable<unknown>>;
  actionArg?: NonNullable<unknown>;
  ['data-initialfocus']?: unknown;
  /** This is used for ButtonLinks where the link is to an external site */
  isExternal?: boolean;
  ariaLabel?: string;
  unstyled?: boolean;
} & HTMLButtonAttributes;

const getButtonColorClass = (color: ButtonColor) => {
  switch (color) {
    case 'white':
      return clsx(
        'bg-st-gray-200',
        'disabled:bg-st-gray-400 disabled:text-st-gray-500',
        'active:bg-st-gray-300',
        'hover:bg-st-gray-400',
      );
    case 'brand':
      return clsx(
        'bg-st-yellow-500',
        'disabled:bg-st-gray-400 disabled:text-st-gray-500',
        'active:bg-st-yellow-600',
        'hover:bg-st-yellow-700',
      );
    case 'dark':
      return clsx(
        'bg-st-gray-800 text-st-white',
        'disabled:bg-st-gray-400 disabled:text-st-gray-500',
        'active:bg-st-gray-600',
        'hover:bg-st-gray-700',
      );
    case 'transparent':
      return undefined;
    default:
      return undefined;
  }
};

export function getVariantClassName(color: ButtonColor) {
  const variants = getButtonColorClass(color);
  return clsx(variants);
}
